/**
 * Client-side MP4 spatial-metadata injector.
 *
 * Writes Google Spatial Media v2 boxes directly into an MP4 file so that
 * YouTube VR, Cardboard Player, Skybox VR, and other spec-compliant players
 * auto-detect the stereo layout and projection without any filename cue.
 *
 * Supported boxes:
 *   st3d (Stereoscopic 3D) — inserted into the video sample entry
 *       stereo_mode: 0=mono, 1=top-bottom, 2=left-right
 *   sv3d (Spherical Video 3D) — also inside sample entry
 *       svhd + proj{prhd + equi} for equirectangular projection
 *
 * FFmpeg silently drops -metadata:s:v:0 stereo_mode=... when muxing to MP4, so
 * this runs as a post-process on the encoder's output — only a few boxes are
 * touched, nothing is re-encoded.
 *
 * Critical detail: files with -movflags +faststart have moov BEFORE mdat, so
 * inserting 13 bytes into moov shifts every byte in mdat. The stco/co64 chunk
 * offset tables inside moov must therefore be patched — otherwise playback
 * breaks. This module does that.
 *
 * ⚠️ THE FILE IS NEVER READ WHOLE (since 17.09.2026). Chunk offsets are absolute
 * file positions and the only bytes that change live inside moov, so the
 * injection is «read moov · patch moov · Blob of [head slice, new moov, tail
 * slice]». Until then injectBlob did `blob.arrayBuffer()` + a full copy + a
 * third copy as a Blob: measured on /video-to-vr with a 1.6 GB source, the
 * 1.4 GB side-by-side output cost 6.2 GB of RSS after the export (the
 * hardware lane itself peaks at ~1.4 GB), and an output above 2 GiB could not
 * be read at all (one ArrayBuffer cannot be that large). The fast lane muxes
 * into OPFS precisely so the output is not a heap term — this step must not
 * make it one.
 *
 * Exposes window.VRMetadataInjector:
 *   injectBlob(blob, opts) → Blob   (the front door: slices, no whole-file read)
 *   inject(uint8Array, opts) → Uint8Array   (whole-buffer form, same result)
 *   injectMoov(moovBytes, moovFileStart, opts) → Uint8Array   (the core)
 *
 * References:
 *   ISO/IEC 14496-12 (MP4 container)
 *   https://github.com/google/spatial-media/blob/master/docs/spatial-video-v2-rfc.md
 */
(function () {
    'use strict';

    /* ── low-level helpers ─────────────────────────────────────────────── */

    function readU32(bytes, offset) {
        return (bytes[offset] << 24 | bytes[offset + 1] << 16 | bytes[offset + 2] << 8 | bytes[offset + 3]) >>> 0;
    }
    function writeU32(bytes, offset, value) {
        bytes[offset]     = (value >>> 24) & 0xff;
        bytes[offset + 1] = (value >>> 16) & 0xff;
        bytes[offset + 2] = (value >>> 8)  & 0xff;
        bytes[offset + 3] = value & 0xff;
    }
    function readU64(bytes, offset) {
        /* 64-bit sizes appear only on very large boxes; read as two halves and
           combine — exact up to Number.MAX_SAFE_INTEGER, far beyond any file a
           browser handles here. */
        const hi = readU32(bytes, offset);
        const lo = readU32(bytes, offset + 4);
        return hi * 0x100000000 + lo;
    }
    function readType(bytes, offset) {
        return String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);
    }
    function writeType(bytes, offset, type) {
        bytes[offset]     = type.charCodeAt(0);
        bytes[offset + 1] = type.charCodeAt(1);
        bytes[offset + 2] = type.charCodeAt(2);
        bytes[offset + 3] = type.charCodeAt(3);
    }

    /* ── box walker ────────────────────────────────────────────────────── */

    /* Parse direct children of a region [start, end). Returns entries that
       also capture header length so callers can locate the data-after-header
       offset without recomputing. */
    function parseChildren(bytes, start, end) {
        const out = [];
        let p = start;
        while (p + 8 <= end) {
            const size = readU32(bytes, p);
            const type = readType(bytes, p + 4);
            let actual = size, header = 8;
            if (size === 1) {
                actual = readU64(bytes, p + 8);
                header = 16;
            } else if (size === 0) {
                actual = end - p;       /* box extends to end of parent */
            }
            if (actual < header || p + actual > end) break;
            out.push({ type, start: p, size: actual, header, dataStart: p + header, dataEnd: p + actual });
            p += actual;
        }
        return out;
    }

    /* Find descendant by path like ['moov','trak','mdia']. Only returns first
       match (first trak etc). For multi-trak cases the caller passes a
       predicate that identifies the right one (e.g. handler_type='vide'). */
    function findPath(bytes, root, path, predicate) {
        let current = [root];
        for (let i = 0; i < path.length; i++) {
            const wanted = path[i];
            const next = [];
            for (const box of current) {
                const children = parseChildren(bytes, box.dataStart, box.dataEnd);
                for (const child of children) {
                    if (child.type !== wanted) continue;
                    if (i === path.length - 1 && predicate && !predicate(child, bytes)) continue;
                    next.push(child);
                }
            }
            current = next;
        }
        return current[0] || null;
    }

    /* Top-level boxes of a Blob, read header by header — 8–16 bytes per box,
       the payload is never touched. */
    async function topLevelBoxes(blob) {
        const out = [];
        let p = 0, guard = 0;
        while (p + 8 <= blob.size && guard++ < 4096) {
            const h = new Uint8Array(await blob.slice(p, Math.min(blob.size, p + 16)).arrayBuffer());
            if (h.byteLength < 8) break;
            let size = readU32(h, 0), header = 8;
            const type = readType(h, 4);
            if (size === 1) {
                if (h.byteLength < 16) break;
                size = readU64(h, 8); header = 16;
            } else if (size === 0) {
                size = blob.size - p;
            }
            if (size < header || p + size > blob.size) break;
            out.push({ type, start: p, size });
            p += size;
        }
        return out;
    }

    /* ── box builders ──────────────────────────────────────────────────── */

    function buildSt3d(stereoMode) {
        /* FullBox header (8+4 = 12 bytes) + 1 byte stereo_mode = 13 bytes total */
        const b = new Uint8Array(13);
        writeU32(b, 0, 13);
        writeType(b, 4, 'st3d');
        /* version(1)+flags(3) = 0 at offsets 8..11 */
        b[12] = stereoMode & 0xff;
        return b;
    }

    function buildSv3dMonoEquirect() {
        /* sv3d {
             svhd (FullBox + metadata_source null-terminated UTF-8) — we use "Timbrica\0"
             proj {
               prhd (FullBox + 3× fixed-point pose values, all zero)
               equi (FullBox + 4× zero padding — full-sphere, no crop)
             }
           } */
        const metadataSource = 'Timbrica\0';
        const svhdData = 4 + metadataSource.length;        /* version/flags + string */
        const svhdSize = 8 + svhdData;                     /* 8 header + payload */

        const prhdSize = 8 + 4 + 12;                       /* FullBox + 3 i32 = 24 */
        const equiSize = 8 + 4 + 16;                       /* FullBox + 4 u32 padding = 28 */
        const projSize = 8 + prhdSize + equiSize;          /* container header + children = 60 */
        const sv3dSize = 8 + svhdSize + projSize;

        const b = new Uint8Array(sv3dSize);
        let p = 0;

        /* sv3d */
        writeU32(b, p, sv3dSize); writeType(b, p + 4, 'sv3d'); p += 8;

        /* svhd (FullBox v0, flags=0) */
        writeU32(b, p, svhdSize); writeType(b, p + 4, 'svhd'); p += 8;
        /* version+flags = 0 */ p += 4;
        for (let i = 0; i < metadataSource.length; i++) b[p + i] = metadataSource.charCodeAt(i);
        p += metadataSource.length;

        /* proj (container) */
        writeU32(b, p, projSize); writeType(b, p + 4, 'proj'); p += 8;

        /* prhd (pose all zero) */
        writeU32(b, p, prhdSize); writeType(b, p + 4, 'prhd'); p += 8;
        p += 4;      /* version/flags zero */
        p += 12;     /* yaw/pitch/roll all zero */

        /* equi (equirectangular projection — full sphere, zero crop padding) */
        writeU32(b, p, equiSize); writeType(b, p + 4, 'equi'); p += 8;
        p += 4;      /* version/flags zero */
        p += 16;     /* projection_bounds_top/bottom/left/right = 0 */

        return b;
    }

    /* ── chunk-offset table collection ─────────────────────────────────── */

    /* Scan every trak in moov and return a flat list of chunk-offset tables
       (stco or co64) along with where their entries live. Reading is done on
       the ORIGINAL buffer so box sizes are correct. Later we apply patches to
       the NEW buffer using these original locations plus the insertion shift. */
    function collectChunkOffsetTables(bytes, moov) {
        const tables = [];
        const traks = parseChildren(bytes, moov.dataStart, moov.dataEnd).filter(b => b.type === 'trak');
        for (const trak of traks) {
            const stbl = findPath(bytes, trak, ['mdia', 'minf', 'stbl']);
            if (!stbl) continue;
            const stblChildren = parseChildren(bytes, stbl.dataStart, stbl.dataEnd);
            for (const box of stblChildren) {
                if (box.type !== 'stco' && box.type !== 'co64') continue;
                const entryCount = readU32(bytes, box.dataStart + 4);
                const entriesStart = box.dataStart + 8;
                const elemSize = (box.type === 'co64') ? 8 : 4;
                tables.push({ type: box.type, entriesStart, entryCount, elemSize });
            }
        }
        return tables;
    }

    /* ── the core: patch ONE moov box ──────────────────────────────────── */

    /* moovBytes      — the moov box exactly (header included), as it sits in the file
       moovFileStart  — the moov's absolute position in that file
         opts.stereoMode: 'left_right' | 'top_bottom' | 'mono' | null
         opts.projection: 'equirectangular' | null
       Returns a NEW Uint8Array with the metadata, or `moovBytes` itself when
       there is nothing to do. Throws on a malformed container so the caller
       can fall back to the plain output.

       Chunk offsets (stco/co64) are ABSOLUTE file positions. Bytes at or past
       the insertion point move by the injected length, and a chunk before it
       (mdat ahead of moov — the streaming layout our own muxer writes) does
       not move at all. The file position of the insertion is
       moovFileStart + insertAt, and that is what every offset is compared
       against — the same rule the whole-buffer form always applied. */
    function injectMoov(moovBytes, moovFileStart, opts) {
        opts = opts || {};
        moovFileStart = +moovFileStart || 0;

        const root = parseChildren(moovBytes, 0, moovBytes.byteLength);
        const moov = root.find(b => b.type === 'moov');
        if (!moov || moov.start !== 0) throw new Error('MP4 injector: no moov box');

        /* Descend to the video sample entry (avc1, hev1, hvc1, etc.). Not a
           fixed name — we locate by handler_type='vide'. */
        const trak = findPath(moovBytes, moov, ['trak'], (trakBox) => {
            const hdlr = findPath(moovBytes, trakBox, ['mdia', 'hdlr']);
            if (!hdlr) return false;
            /* hdlr layout: FullBox(4) + pre_defined(4) + handler_type(4) + ... */
            const handlerType = readType(moovBytes, hdlr.dataStart + 8);
            return handlerType === 'vide';
        });
        if (!trak) throw new Error('MP4 injector: no video trak');

        const stsd = findPath(moovBytes, trak, ['mdia', 'minf', 'stbl', 'stsd']);
        if (!stsd) throw new Error('MP4 injector: no stsd');

        /* stsd layout: FullBox (4) + entry_count (4) + first sample entry (variable).
           The sample entry (avc1/hev1) is itself a box we can expand. */
        const sampleEntries = parseChildren(moovBytes, stsd.dataStart + 8, stsd.dataEnd);
        if (!sampleEntries.length) throw new Error('MP4 injector: no sample entry in stsd');
        const codecBox = sampleEntries[0];

        /* Build metadata payload. For SBS, we need st3d only. For mono 360°,
           we need st3d (mono) + sv3d (equi). For sbs180, we treat it as SBS
           with sv3d still mono-sphere since the stereo is conveyed by st3d. */
        const chunks = [];
        let stereoMode = null;
        if (opts.stereoMode === 'left_right')   stereoMode = 2;
        else if (opts.stereoMode === 'top_bottom') stereoMode = 1;
        else if (opts.stereoMode === 'mono')       stereoMode = 0;
        if (stereoMode != null) chunks.push(buildSt3d(stereoMode));
        if (opts.projection === 'equirectangular') chunks.push(buildSv3dMonoEquirect());
        if (!chunks.length) return moovBytes;   /* nothing to do */

        /* Insert BEFORE codecBox end (i.e. at its dataEnd). This puts new
           child boxes as trailing children of avc1/hev1, which is valid per
           Spatial Media spec. */
        const insertAt = codecBox.dataEnd;                 /* local to moovBytes */
        const insertFilePos = moovFileStart + insertAt;    /* absolute in the file */
        const totalInject = chunks.reduce((s, c) => s + c.byteLength, 0);

        /* Collect every chunk-offset table BEFORE mutating the buffer, using
           original box sizes. Each entry knows its original position and will
           be patched after the splice at position + shiftOf(position). */
        const tables = collectChunkOffsetTables(moovBytes, moov);

        /* Allocate the new moov and splice the injection at insertAt. */
        const out = new Uint8Array(moovBytes.byteLength + totalInject);
        out.set(moovBytes.subarray(0, insertAt), 0);
        let wp = insertAt;
        for (const c of chunks) { out.set(c, wp); wp += c.byteLength; }
        out.set(moovBytes.subarray(insertAt), wp);

        /* For any position inside moov at or past insertAt, its new location is +totalInject. */
        const shiftOf = (pos) => pos >= insertAt ? totalInject : 0;

        /* Patch chunk offset VALUES: an offset that pointed at or past the
           insertion point in the FILE grows by the injected length. The entry's
           own location inside moov may have shifted too, so it is read and
           written at the shifted position of the new buffer. */
        for (const tab of tables) {
            const newEntriesStart = tab.entriesStart + shiftOf(tab.entriesStart);
            for (let i = 0; i < tab.entryCount; i++) {
                const entryPosNew = newEntriesStart + i * tab.elemSize;
                if (tab.elemSize === 4) {
                    const v = readU32(out, entryPosNew);
                    if (v >= insertFilePos) writeU32(out, entryPosNew, v + totalInject);
                } else {
                    const hi = readU32(out, entryPosNew);
                    const lo = readU32(out, entryPosNew + 4);
                    const v = hi * 0x100000000 + lo;
                    if (v >= insertFilePos) {
                        const nv = v + totalInject;
                        writeU32(out, entryPosNew,     Math.floor(nv / 0x100000000));
                        writeU32(out, entryPosNew + 4, nv >>> 0);
                    }
                }
            }
        }

        /* Update ancestor box sizes: every ancestor that contains insertAt
           gains `totalInject` bytes. Each ancestor's header is at its original
           .start offset (BEFORE insertAt), so no shift needed for the header
           write position. */
        const ancestors = [moov, trak].concat([
            findPath(moovBytes, trak, ['mdia']),
            findPath(moovBytes, trak, ['mdia', 'minf']),
            findPath(moovBytes, trak, ['mdia', 'minf', 'stbl']),
            stsd,
            codecBox,
        ]).filter(Boolean);

        for (const a of ancestors) {
            if (a.size === 1 || a.header === 16) {
                /* 64-bit largesize at a.start + 8..15 */
                const hi = readU32(out, a.start + 8);
                const lo = readU32(out, a.start + 12);
                const newSize = hi * 0x100000000 + lo + totalInject;
                writeU32(out, a.start + 8,  Math.floor(newSize / 0x100000000));
                writeU32(out, a.start + 12, newSize >>> 0);
            } else {
                writeU32(out, a.start, a.size + totalInject);
            }
        }

        return out;
    }

    /* ── whole-buffer form (compatibility; same bytes as the Blob form) ─── */

    function inject(bytes, opts) {
        const rootChildren = parseChildren(bytes, 0, bytes.byteLength);
        const moov = rootChildren.find(b => b.type === 'moov');
        if (!moov) throw new Error('MP4 injector: no moov box');
        const moovBytes = bytes.subarray(moov.start, moov.start + moov.size);
        const patched = injectMoov(moovBytes, moov.start, opts);
        if (patched === moovBytes) return bytes;   /* nothing to do */
        const out = new Uint8Array(bytes.byteLength - moov.size + patched.byteLength);
        out.set(bytes.subarray(0, moov.start), 0);
        out.set(patched, moov.start);
        out.set(bytes.subarray(moov.start + moov.size), moov.start + patched.byteLength);
        return out;
    }

    /* ── the front door: a Blob in, a Blob of slices out ───────────────── */

    async function injectBlob(blob, opts) {
        const boxes = await topLevelBoxes(blob);
        const moov = boxes.find(b => b.type === 'moov');
        if (!moov) throw new Error('MP4 injector: no moov box');
        const moovBytes = new Uint8Array(await blob.slice(moov.start, moov.start + moov.size).arrayBuffer());
        const patched = injectMoov(moovBytes, moov.start, opts);
        if (patched === moovBytes) return blob;    /* nothing to do — the same file */
        return new Blob(
            [blob.slice(0, moov.start), patched, blob.slice(moov.start + moov.size)],
            { type: blob.type || 'video/mp4' }
        );
    }

    window.VRMetadataInjector = { inject, injectBlob, injectMoov };
})();
